import type { UploadProgressValues } from '@frameio/web-uploader';
import { UploadProgress } from '@frameio/web-uploader';
import type { TransferBatch } from '../types/uploader';
import { isQueued, isTransferring } from './helper';

type IncompleteJobsSplit = {
  bytesTotal: number;
  bytesSent: number;
  currentBytesPerSecond: number;
  queuedBytesTotal: number;
  slowestJob: number;
};

/**
 *
 * @param jobs is expected to be  incomplete jobs: queued, idle, or transfering.
 * @returns an UploadProgress, with the slowest time out of all transfering jobs
 */
export function getTotalProgress(jobs: TransferBatch[]): UploadProgressValues {
  const {
    bytesTotal,
    bytesSent,
    currentBytesPerSecond,
    queuedBytesTotal,
    slowestJob,
  }: IncompleteJobsSplit = jobs.reduce(
    (acc, job) => {
      /* eslint-disable no-param-reassign */
      if (isTransferring(job) && !job.isPaused) {
        acc.bytesTotal += job.progress?.bytesTotal || 0;
        acc.bytesSent += job.progress?.bytesSent || 0;
        acc.currentBytesPerSecond += job.progress?.bytesPerSecond || 0;
        acc.slowestJob = Math.max(
          acc.slowestJob,
          job.progress?.remainingTime || 0
        );
      } else if (isQueued(job)) {
        acc.queuedBytesTotal += job.progress?.bytesTotal || 0;
      }
      /* eslint-enable no-param-reassign */
      return acc;
    },
    {
      bytesTotal: 0,
      bytesSent: 0,
      currentBytesPerSecond: 0,
      queuedBytesTotal: 0,
      /**
       * Each task is uploading with an estimated speed.
       * We want to represent whichever job has the slowest speed.
       * Collecting the sum of all concurrent jobs shows a total time longer
       * than we actually should expect.
       */
      slowestJob: 0,
    }
  );

  const currentProgress = new UploadProgress({
    bytesSent,
    bytesTotal: bytesTotal + queuedBytesTotal,
    bytesPerSecond: currentBytesPerSecond,
  }).values;

  const queuedEstimate = new UploadProgress({
    // Avoid `Infinity` by spoofing the UploadProgress.
    bytesSent: 1,
    bytesTotal: queuedBytesTotal,
    bytesPerSecond: currentBytesPerSecond,
  }).values;

  let remainingTime = slowestJob;
  /**
   * If any jobs are queued the combined calculation of
   * queued time and slowest job is more accurate than using just
   * the just slowest job.
   */
  if (queuedBytesTotal > 0) remainingTime += queuedEstimate.remainingTime;

  return {
    ...currentProgress,
    remainingTime,
  };
}
